import { useEffect, useEffectEvent, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CredentialResponse } from "@react-oauth/google";

import { useAppDispatch } from "../../state/hooks";
import { loginWithLinkedIn, loginWithSso, SignInResult } from "../../state/actions/authActions";
import { SsoProviders } from "../../ajax/UserAccountAjax";
import { API_BASE_URL } from "../../config";
import { getMsalClient } from "./msalConfig";
import { LinkedInRedirect } from "./linkedInRedirect";
import {
  beginLinkedInSignIn, beginMicrosoftSignIn, readSsoArrival, rememberChosenZones, takeChosenZones, takeLinkedInNonce, takeMicrosoftSignIn,
} from "./ssoRedirect";
import { leaveFor } from "./leaveFor";

type MicrosoftSignIn = NonNullable<SsoProviders["microsoft"]>;

const MICROSOFT_FAILED = "Microsoft sign-in was cancelled or failed.";

export function useSsoSignIn(
  { microsoft, linkedIn }: SsoProviders,
  onStart: () => void,
  onResult: (result: SignInResult) => void,
) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const arrivalHandled = useRef(false);

  const startMicrosoft = async (signIn: MicrosoftSignIn, zoneIds: number[]) => {
    rememberChosenZones(zoneIds);
    onStart();
    try {
      const msal = getMsalClient(signIn.clientId, signIn.authority);
      await msal.ready;
      await msal.instance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      beginMicrosoftSignIn();
      await msal.instance.loginRedirect({ scopes: ["openid", "profile", "email"] });
    } catch {
      onResult({ ok: false, error: MICROSOFT_FAILED });
    }
  };

  // Note: MSAL hands back its first redirect result for the life of the page, so without this check signing out signs you straight back in
  const finishMicrosoft = async (signIn: MicrosoftSignIn) => {
    if (!takeMicrosoftSignIn()) return;

    try {
      const msal = getMsalClient(signIn.clientId, signIn.authority);
      await msal.ready;
      const result = await msal.instance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      if (!result) return;

      onStart();
      onResult(await dispatch(loginWithSso("Microsoft", result.idToken, takeChosenZones())));
    } catch {
      onResult({ ok: false, error: MICROSOFT_FAILED });
    }
  };

  const startLinkedIn = (zoneIds: number[]) => {
    rememberChosenZones(zoneIds);
    onStart();
    leaveFor(`${API_BASE_URL}/User/external-login/linkedin/start?nonce=${encodeURIComponent(beginLinkedInSignIn())}`);
  };

  const finishLinkedIn = async (redirect: LinkedInRedirect) => {
    if (!redirect.ok) {
      onResult({ ok: false, error: redirect.error });
      return;
    }

    onStart();
    onResult(await dispatch(loginWithLinkedIn(redirect.code, redirect.state, takeLinkedInNonce(), takeChosenZones())));
  };

  const signInWithGoogle = async (credential: CredentialResponse) => {
    if (!credential.credential) {
      onResult({ ok: false, error: "Google sign-in did not return a token." });
      return;
    }

    onStart();
    onResult(await dispatch(loginWithSso("Google", credential.credential)));
  };

  const handleArrival = useEffectEvent(() => {
    const arrival = readSsoArrival(location.search);
    if (arrival.kind !== "none") navigate("/login", { replace: true });

    switch (arrival.kind) {
      case "linkedIn":
        void finishLinkedIn(arrival.redirect);
        break;
      case "signup":
        if (arrival.request.provider === "LinkedIn" && linkedIn) {
          startLinkedIn(arrival.request.zoneIds);
        } else if (arrival.request.provider === "Microsoft" && microsoft) {
          void startMicrosoft(microsoft, arrival.request.zoneIds);
        } else {
          rememberChosenZones(arrival.request.zoneIds);
          onResult({ ok: false, error: `Sign-in with ${arrival.request.provider} is not available here. Please use one of the options below.` });
        }
        break;
      case "none":
        if (microsoft) void finishMicrosoft(microsoft);
        break;
    }
  });

  useEffect(() => {
    if (arrivalHandled.current) return;
    arrivalHandled.current = true;
    handleArrival();
  }, []);

  return {
    signInWithMicrosoft: () => {
      if (microsoft) void startMicrosoft(microsoft, takeChosenZones());
    },
    signInWithLinkedIn: () => startLinkedIn(takeChosenZones()),
    signInWithGoogle,
    googleFailed: () => onResult({ ok: false, error: "Google sign-in failed." }),
  };
}
